import { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  DevHubBox,
  DevHubTypography,
  DevHubDynamicIcon,
  DevHubButton,
} from '@devxdiscover/devhub-ui';
import { ExperimentalApplication } from './types';
import { IdeaCard } from './IdeaCard';

const useStyles = makeStyles(theme => ({
  navigationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  navigationButtonWrapper: {
    '& button': {
      minWidth: 120,
      height: 40,
      borderRadius: 4,
      border: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
      '&:hover': {
        backgroundColor: '#f0f0f0',
        borderColor: theme.palette.primary.main,
      },
      '&:disabled': {
        opacity: 0.3,
        cursor: 'not-allowed',
      },
    },
  },
  indicator: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    minWidth: 100,
  },
  contentArea: {
    width: '100%',
  },
}));

interface IdeaCarouselProps {
  ideas: ExperimentalApplication[];
  onRefresh?: (applicationId: number) => void;
  onDelete?: (applicationId: number) => void;
  refreshingAppId?: number | null;
  /** Whether to render navigation outside for fixed positioning */
  externalNavigation?: boolean;
}

/**
 * Carousel component for displaying experimental application ideas one at a time
 * @param props - Component props
 * @param props.ideas - Array of experimental applications to display
 * @param props.onRefresh - Optional callback when an idea is refreshed
 * @param props.onDelete - Optional callback when an idea is deleted
 * @param props.refreshingAppId - ID of the application currently being refreshed
 * @param props.externalNavigation - Whether to render navigation outside the carousel for fixed positioning
 */
export const IdeaCarousel = ({
  ideas,
  onRefresh,
  onDelete,
  refreshingAppId = null,
  externalNavigation = false,
}: IdeaCarouselProps) => {
  const classes = useStyles();

  // Sort ideas by date (newest first)
  const sortedIdeas = [...ideas].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? sortedIdeas.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev === sortedIdeas.length - 1 ? 0 : prev + 1));
  };

  if (sortedIdeas.length === 0) {
    return (
      <DevHubBox padding="medium" textAlign="center">
        <DevHubTypography variant="body1">
          No ideas yet. Click "Onboard an Idea" to get started!
        </DevHubTypography>
      </DevHubBox>
    );
  }

  const currentIdea = sortedIdeas[currentIndex];

  const navigationBar =
    sortedIdeas.length > 1 ? (
      <div className={classes.navigationBar}>
        <div className={classes.navigationButtonWrapper}>
          <DevHubButton
            onClick={handlePrevious}
            variant="outlined"
            startIcon={<DevHubDynamicIcon iconName="ArrowBackIos" />}
          >
            Previous
          </DevHubButton>
        </div>

        <div className={classes.indicator}>
          <DevHubTypography variant="body2" style={{ fontWeight: 500 }}>
            {currentIndex + 1} of {sortedIdeas.length}
          </DevHubTypography>
        </div>

        <div className={classes.navigationButtonWrapper}>
          <DevHubButton
            onClick={handleNext}
            variant="outlined"
            endIcon={<DevHubDynamicIcon iconName="ArrowForwardIosOutlined" />}
          >
            Next
          </DevHubButton>
        </div>
      </div>
    ) : null;

  return (
    <>
      {externalNavigation && navigationBar}
      <div>
        {!externalNavigation && navigationBar}

        <div className={classes.contentArea}>
          <IdeaCard
            idea={currentIdea}
            onRefresh={onRefresh}
            onDelete={onDelete}
            refreshing={refreshingAppId === currentIdea.applicationId}
            showActions={false}
            size="small"
          />
        </div>
      </div>
    </>
  );
};
